/**
 * Supabase browser client — uses the anon/public key.
 * Session persistence is handled by Supabase using a storage adapter that
 * supports both durable "Remember Me" sessions and tab-scoped sessions.
 * Never use the service-role key here; that lives in the backend only.
 *
 * When VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not set (e.g. in local
 * bootstrap/dev mode or CI without Supabase credentials), `isAuthConfigured`
 * is false and the auth guards will skip authentication, preserving the
 * original single-user behaviour.
 */
import { createClient } from '@supabase/supabase-js';
import type { SupportedStorage } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/** True only when both env vars are present — used to conditionally enable auth. */
export const isAuthConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

type AuthStorageMode = 'local' | 'session';
type BrowserStorageName = AuthStorageMode | 'both';

const REMEMBER_ME_STORAGE_KEY = 'navyrx.auth.remember_me';
const PLACEHOLDER_AUTH_STORAGE_KEY = 'sb-placeholder-auth-token';

function getSupabaseAuthStorageKey() {
  if (!SUPABASE_URL) return PLACEHOLDER_AUTH_STORAGE_KEY;
  try {
    const url = new URL(SUPABASE_URL);
    return `sb-${url.hostname.split('.')[0]}-auth-token`;
  } catch {
    return PLACEHOLDER_AUTH_STORAGE_KEY;
  }
}

export const SUPABASE_AUTH_STORAGE_KEY = getSupabaseAuthStorageKey();

const SUPABASE_AUTH_STORAGE_KEYS = [
  SUPABASE_AUTH_STORAGE_KEY,
  `${SUPABASE_AUTH_STORAGE_KEY}-code-verifier`,
  `${SUPABASE_AUTH_STORAGE_KEY}-user`,
];

function getBrowserStorage(mode: AuthStorageMode): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    return mode === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function safeGetItem(storage: Storage | null, key: string): string | null {
  if (!storage) return null;

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage: Storage | null, key: string, value: string): boolean {
  if (!storage) return false;

  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemoveItem(storage: Storage | null, key: string) {
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage availability failures; Supabase still keeps in-memory state.
  }
}

function getStoredRememberMePreference(): boolean | null {
  const value = safeGetItem(getBrowserStorage('local'), REMEMBER_ME_STORAGE_KEY);
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

let activeAuthStorageMode: AuthStorageMode =
  getStoredRememberMePreference() === true ? 'local' : 'session';

export function getRememberMePreference(): boolean {
  return getStoredRememberMePreference() === true;
}

export function setRememberMePreference(rememberMe: boolean) {
  activeAuthStorageMode = rememberMe ? 'local' : 'session';
  safeSetItem(getBrowserStorage('local'), REMEMBER_ME_STORAGE_KEY, rememberMe ? 'true' : 'false');
}

export function clearRememberMePreference() {
  activeAuthStorageMode = 'session';
  safeRemoveItem(getBrowserStorage('local'), REMEMBER_ME_STORAGE_KEY);
}

export function clearSupabaseAuthSession(storageName: BrowserStorageName = 'both') {
  const storages =
    storageName === 'both'
      ? [getBrowserStorage('local'), getBrowserStorage('session')]
      : [getBrowserStorage(storageName)];

  for (const storage of storages) {
    for (const key of SUPABASE_AUTH_STORAGE_KEYS) {
      safeRemoveItem(storage, key);
    }
  }
}

export const rememberMeAuthStorage: SupportedStorage = {
  getItem: (key) => {
    const preference = getStoredRememberMePreference();
    const primaryMode: AuthStorageMode = preference === true ? 'local' : 'session';
    const secondaryMode: AuthStorageMode = primaryMode === 'local' ? 'session' : 'local';
    const primaryValue = safeGetItem(getBrowserStorage(primaryMode), key);

    if (primaryValue !== null) {
      activeAuthStorageMode = primaryMode;
      return primaryValue;
    }

    if (preference !== false) {
      const secondaryValue = safeGetItem(getBrowserStorage(secondaryMode), key);
      if (secondaryValue !== null) {
        activeAuthStorageMode = secondaryMode;
        return secondaryValue;
      }
    }

    return null;
  },
  setItem: (key, value) => {
    const target = getBrowserStorage(activeAuthStorageMode);
    const fallbackMode: AuthStorageMode = activeAuthStorageMode === 'local' ? 'session' : 'local';
    const fallback = getBrowserStorage(fallbackMode);
    const wroteToTarget = safeSetItem(target, key, value);

    if (!wroteToTarget && safeSetItem(fallback, key, value)) {
      activeAuthStorageMode = fallbackMode;
    }

    safeRemoveItem(getBrowserStorage(activeAuthStorageMode === 'local' ? 'session' : 'local'), key);
  },
  removeItem: (key) => {
    safeRemoveItem(getBrowserStorage('local'), key);
    safeRemoveItem(getBrowserStorage('session'), key);
  },
};

if (!isAuthConfigured) {
  // During local dev, copy .env.example → .env and fill in the values.
  if (import.meta.env.DEV) {
    console.warn(
      '[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is not set. ' +
        'Running in bootstrap mode — authentication is disabled.',
    );
  }
}

// When env vars are missing createClient('', '') throws "supabaseUrl is required."
// which crashes the module and leaves the app completely blank.  Guard the call
// and fall back to a placeholder that is never actually invoked (all callers
// check `isAuthConfigured` first).
export const supabase = isAuthConfigured
  ? createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: SUPABASE_AUTH_STORAGE_KEY,
        storage: rememberMeAuthStorage,
      },
    })
  : createClient('https://placeholder.supabase.co', 'placeholder-anon-key', {
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    });
